import { Injectable } from '@angular/core';
import { ProgressStep } from '@ld.consumer-portal/models';

interface ProgressCacheEntry {
  maxStepIndex: number;
}

interface ProgressCacheMap {
  [loanId: string]: ProgressCacheEntry;
}

@Injectable({ providedIn: 'root' })
export class OriginationProgressTrackerService {
  private readonly STORAGE_KEY = 'origination_progress_cache';

  reconcileSteps(loanId: string, backendSteps: ProgressStep[]): ProgressStep[] {
    if (!loanId || !backendSteps?.length) {
      return backendSteps || [];
    }

    const cached = this.getCachedProgress(loanId);
    const currentMaxIndex = this.findMaxProgressIndex(backendSteps);

    const maxAchievedIndex = cached ? Math.max(cached.maxStepIndex, currentMaxIndex) : currentMaxIndex;

    if (!cached || maxAchievedIndex > cached.maxStepIndex) {
      this.setCachedProgress(loanId, maxAchievedIndex);
    }

    return this.applyMaxProgress(backendSteps, maxAchievedIndex);
  }

  private findMaxProgressIndex(steps: ProgressStep[]): number {
    let maxIndex = -1;
    steps.forEach((step, index) => {
      if (step.status === 'completed' || step.status === 'current') {
        maxIndex = index;
      }
    });
    return maxIndex;
  }

  private applyMaxProgress(steps: ProgressStep[], maxIndex: number): ProgressStep[] {
    if (maxIndex < 0) {
      return steps;
    }

    return steps.map((step, index) => {
      if (index < maxIndex) {
        return { ...step, status: 'completed' as const };
      } else if (index === maxIndex) {
        return { ...step, status: 'current' as const };
      } else {
        return { ...step, status: 'not-completed' as const };
      }
    });
  }

  private getCachedProgress(loanId: string): ProgressCacheEntry | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return null;

      const cacheMap: ProgressCacheMap = JSON.parse(stored);
      const loanCache = cacheMap[loanId];

      if (!loanCache) return null;

      return loanCache;
    } catch {
      return null;
    }
  }

  private setCachedProgress(loanId: string, maxStepIndex: number): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      const cacheMap: ProgressCacheMap = stored ? JSON.parse(stored) : {};

      cacheMap[loanId] = {
        maxStepIndex,
      };

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cacheMap));
    } catch {
      // Silently fail if localStorage is unavailable
    }
  }

  clearProgress(loanId: string): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const cacheMap: ProgressCacheMap = JSON.parse(stored);
        delete cacheMap[loanId];
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cacheMap));
      }
    } catch {
      // Silently fail
    }
  }

  clearAllProgress(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch {
      // Silently fail
    }
  }
}
